import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import {
  recipeAPI,
  type RecipeListParams,
  type RecipeWritePayload,
} from "@/lib/recipe-api";

export const useRecipes = (params?: RecipeListParams) => {
  return useQuery({
    queryKey: queryKeys.nutrition.recipes.list(params),
    queryFn: () => recipeAPI.list(params),
    staleTime: 1000 * 30,
    // Keep the prior page visible while a new filter/search/sort refetches so
    // the table doesn't blank out and jump on every keystroke or tab switch.
    placeholderData: keepPreviousData,
  });
};

export const useRecipe = (id?: string | null) => {
  return useQuery({
    queryKey: queryKeys.nutrition.recipes.detail(id || ""),
    queryFn: () => recipeAPI.get(id || ""),
    enabled: Boolean(id),
  });
};

export const useCreateRecipe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RecipeWritePayload) => recipeAPI.create(payload),
    onSuccess: (recipe) => {
      queryClient.setQueryData(
        queryKeys.nutrition.recipes.detail(recipe.id),
        recipe
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.nutrition.recipes.all(),
      });
    },
  });
};

export const useUpdateRecipe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      version,
      payload,
    }: {
      id: string;
      version: number;
      payload: RecipeWritePayload;
    }) => recipeAPI.update(id, version, payload),
    onSuccess: (recipe) => {
      queryClient.setQueryData(
        queryKeys.nutrition.recipes.detail(recipe.id),
        recipe
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.nutrition.recipes.all(),
      });
    },
  });
};

export const useSubmitRecipe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      version,
      idempotencyKey,
    }: {
      id: string;
      version: number;
      idempotencyKey: string;
    }) => recipeAPI.submit(id, version, idempotencyKey),
    onSuccess: (recipe) => {
      queryClient.setQueryData(
        queryKeys.nutrition.recipes.detail(recipe.id),
        recipe
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.nutrition.recipes.all(),
      });
    },
  });
};

export const useDiscardRecipe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      recipeAPI.discard(id, version),
    onSuccess: (recipe) => {
      queryClient.setQueryData(
        queryKeys.nutrition.recipes.detail(recipe.id),
        recipe
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.nutrition.recipes.all(),
      });
    },
  });
};

export const useDeleteRecipe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => recipeAPI.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({
        queryKey: queryKeys.nutrition.recipes.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.nutrition.recipes.all(),
      });
    },
  });
};
